import React, { memo, useCallback, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import { Card } from '@iris/react-components/Card';
import clsx from 'clsx';

import { ErrorBoundary } from '@bonnet/next/error-boundary';

import {
    Col,
    ColoredBackground,
    LazyRender,
    Row,
    SubHeading,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';

import contentAnalyticsHandlers from '@/analytics/contentAnalyticsHandlers';

import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import usePreQualEligibility from '@/hooks/usePreQualEligibility';

import CookieConsentModule from '@/modules/CookieConsentModule';
import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

import CardSkeleton from '@/components/CardSkeleton';
import LazyComponent from '@/components/LazyComponent';

const AutoFiPreQual = dynamic(() => import(/* webpackChunkName: "AutoFiPreQual" */ '@/components/vehicledetails/AutoFiPreQual'), { ssr: false });
const WestlakePreQual = dynamic(() => import(/* webpackChunkName: "WestlakePreQual" */ '@/components/Westlake/index.js'), { ssr: false });
const FordMotorCreditPreQual = dynamic(() => import(/* webpackChunkName: "FordMotorCreditPreQual" */ '@/components/vehicledetails/FordMotorCreditPreQual'), { ssr: false });

const autoFiPixallParams = {
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'step',
    feature: 'Autofi',
    label: 'loan',
    vendor: 'Autofi',
};

/**
 * PreQualCTAContainer - Renders third-party pre-qualification calls to action
 *
 * This component displays financing pre-qualification options from multiple lenders including
 * AutoFi, Westlake, and Ford Motor Credit. The component handles different UI contexts,
 * error states, analytics tracking, and conditional rendering based on eligibility.
 *
 * @param {Object} props - Component props
 * @param {string} props.uiContext - UI context that determines layout and behavior
 *   - 'styled': Full layout with copy text and colored background
 *   - 'section': Section layout with copy above content
 *   - 'price-section': New price section layout (shows Westlake CTA only if enabled)
 *   - Other values: Right rail layout without background
 *
 * @returns {React.ReactElement|null} Rendered pre-qualification CTAs or null if not eligible
 *
 * */
function PreQualCTAContainer({
    uiContext,
}) {
    const westlakePreQualUrl = useSelector(globalConfigsDuck.selectors.getWestlakePreQualUrl);
    const autoFiPreQualUrl = useSelector(globalConfigsDuck.selectors.getAutoFiPreQualUrl);
    const fordMotorCreditPreQualSource = useSelector(globalConfigsDuck.selectors.getFordMotorCreditPreQualSource);
    const fordMotorCreditPreQualUrl = useSelector(globalConfigsDuck.selectors.getFordMotorCreditPreQualUrl);

    // thirdparty - config feature to enable/disable all third party placements
    // thirdparty_prequal - config features flag to enable/disable for individual integrations

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const isPriceSection = uiContext === 'price-section';
    const isStyled = uiContext === 'styled';

    const fmcPixallParams = {
        dealerCode: 'atfordcpo',
        calculatorType: 'payment',
        eventSource: 'dealership',
        eventType: 'click',
        feature: 'caca',
        label: isStyled ? 'text-link::saca::apply-for-financing::nds' : 'text-link::saca::apply-for-financing::price-section',
        program: 'fba',
        cdl: {
            eventProperties: {
                event: {
                    valueEventCode: 'TDV1',
                },
            },
        },
    };

    const {
        id: inventoryId,
        externalOwnerId = '',
        vin,
    } = listing;

    const personalization = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalization);
    const hasSuppressedCookies = useSelector(CookieConsentModule.duck.selectors.selectHasSuppressedGroup);
    const pageId = useSelector(currentPageNameDuck.selectors.getDuckState);
    const user = useSelector(userDuck.selectors.getDuckState);

    const [errors, setErrors] = useState({
        autofi: false,
        westlake: false,
        fordmotorcredit: false,
    });

    const {
        isPreQualEligible = false,
        isAutoFiEnabled = false,
        isWestlakeEnabled = false,
        isFordMotorCreditEnabled = false,
    } = usePreQualEligibility(listing);

    const onClickTracking = useCallback((epn, pixallData = {}) => {
        const data = {
            epn,
            pixallData,
            uiContext,
            inventoryId,
        };

        return (event) => sendClick(contentAnalyticsHandlers.preQualCTAClick, event, data);
    }, [uiContext, inventoryId]);

    const onFordMotorCreditPreQualClick = useCallback((epn, pixallData = {}) => {
        const data = {
            epn,
            pixallData,
            uiContext,
        };
        return (event) => sendClick(contentAnalyticsHandlers.preQualCTAClick, event, data);
    }, [uiContext]);

    const ctaClassNames = clsx({
        'margin-top-2 margin-top-sm-4': isStyled,
        'margin-bottom-4': (!isStyled && uiContext !== 'section'),
    });

    const handleWestlakeImpression = () => {
        sendImpressions({
            name: 'westlakeCTAImpression',
            data: {
                epn: 'Westlake Financing',
                uiContext,
            },
        });
    };

    const handleError = (component, value = true) => {
        setErrors((prevErrors) => ({
            ...prevErrors,
            [component]: value,
        }));
    };

    // Calculate total enabled components and total error
    const totalEnabled = useMemo(
        () => [isAutoFiEnabled, isWestlakeEnabled, isFordMotorCreditEnabled].filter(Boolean).length,
        [isAutoFiEnabled, isFordMotorCreditEnabled, isWestlakeEnabled]
    );
    const totalError = useMemo(() => Object.keys(errors).filter((key) => errors[key]).length, [errors]);

    // If all enabled components have errors, render nothing
    if (isStyled && totalEnabled === totalError) {
        return null;
    }

    const ctaContent = () => (
        <LazyComponent fallback={null}>
            <ErrorBoundary>
                {isWestlakeEnabled && (
                    <DelayedImpressionObserver
                        onChange={handleWestlakeImpression}
                        key={`preQuals-${listing?.id}-delayed-impression-observer`}
                    >
                        <WestlakePreQual
                            page={pageId}
                            className={ctaClassNames}
                            westlakePreQualUrl={westlakePreQualUrl}
                            listing={listing}
                            personalization={!hasSuppressedCookies && personalization}
                            placement={isStyled ? 'nds' : 'top'}
                            user={user}
                            uiContext={uiContext}
                            onError={() => handleError('westlake')}
                        />
                    </DelayedImpressionObserver>
                )}
                {isAutoFiEnabled && (
                    <AutoFiPreQual
                        className={ctaClassNames}
                        onAutoFiPreQualClick={onClickTracking('Autofi', autoFiPixallParams)}
                        autoFiPreQualUrl={autoFiPreQualUrl}
                        listing={listing}
                        onError={() => handleError('autofi')}
                    />
                )}
                {isFordMotorCreditEnabled && (
                    <FordMotorCreditPreQual
                        className={ctaClassNames}
                        externalOwnerId={externalOwnerId}
                        fordMotorCreditPreQualUrl={fordMotorCreditPreQualUrl}
                        fordMotorCreditPreQualSource={fordMotorCreditPreQualSource}
                        onFordMotorCreditPreQualClick={onFordMotorCreditPreQualClick('FordMotorCredit', fmcPixallParams)}
                        vin={vin}
                        onError={() => handleError('fordmotorcredit')}
                    />
                )}
            </ErrorBoundary>
        </LazyComponent>
    );

    const ctaCopy = () => (
        <>
            <SubHeading size={300}>
                See Your Estimated Monthly Payments
            </SubHeading>
            <p>Found a car you are interested in? Know your financing options before visiting the dealership.</p>
        </>
    );

    const ctaContentWithCopy = () => (
        <ColoredBackground
            id="prequal-section-styled"
            className={clsx({ 'padding-1': uiContext !== 'section' })}
        >
            {uiContext === 'section' ? (
                <>
                    {ctaCopy()}
                    {ctaContent()}
                </>

            ) : (
                <Row>
                    <Col
                        xs={12}
                        sm={6}
                        className="margin-bottom-2 margin-bottom-sm-0"
                    >
                        {ctaCopy()}
                    </Col>

                    <Col
                        xs={12}
                        sm={6}
                    >
                        {ctaContent()}
                    </Col>
                </Row>
            )}
        </ColoredBackground>
    );

    // render new westlake card when uiContext is 'price-section'
    const westlakeCta = () => (
        <Card
            variant="subtle"
            data-cmp="sponsored-insurance-container"
        >
            <Card.Content>
                <LazyComponent
                    fallback={<CardSkeleton />}
                    width="100%"
                >
                    <ErrorBoundary>
                        <DelayedImpressionObserver
                            onChange={handleWestlakeImpression}
                            key={`preQuals-${listing?.id}-delayed-impression-observer`}
                            className="iris:w-full"
                        >
                            <WestlakePreQual
                                page={pageId}
                                className={ctaClassNames}
                                westlakePreQualUrl={westlakePreQualUrl}
                                listing={listing}
                                personalization={!hasSuppressedCookies && personalization}
                                placement={isStyled ? 'nds' : 'top'}
                                uiContext={uiContext}
                                user={user}
                                onError={() => handleError('westlake')}
                            />
                        </DelayedImpressionObserver>
                    </ErrorBoundary>
                </LazyComponent>
            </Card.Content>
        </Card>
    );

    const lazyLoadWrapper = () => (
        <LazyRender>
            {isPriceSection && isWestlakeEnabled ? westlakeCta() : ctaContentWithCopy()}
        </LazyRender>
    );

    // determine whether to render cta's below NDS (lazyLoadWrapper) or in right rail
    if (isPreQualEligible) {
        return isStyled || uiContext === 'section' || isPriceSection ? lazyLoadWrapper() : ctaContent();
    }

    return null;
}

export default memo(PreQualCTAContainer);
